/**
 * Canonical error codes returned by the API in the `code` field of the error envelope:
 *   { code, message, details? }
 * Keep codes stable — the web client branches on them for UX.
 */

export const ErrorCode = {
  // Auth
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_ACCOUNT_LOCKED: 'AUTH_ACCOUNT_LOCKED',
  AUTH_PASSWORD_CHANGE_REQUIRED: 'AUTH_PASSWORD_CHANGE_REQUIRED',
  AUTH_TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
  AUTH_TOKEN_INVALID: 'AUTH_TOKEN_INVALID',
  AUTH_CSRF_MISMATCH: 'AUTH_CSRF_MISMATCH',

  // Authorization
  FORBIDDEN: 'FORBIDDEN',

  // Validation / input
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  PASSWORD_TOO_WEAK: 'PASSWORD_TOO_WEAK',

  // Resources
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  ALREADY_EXISTS: 'ALREADY_EXISTS',

  // Users module
  USERS_EMAIL_TAKEN: 'USERS_EMAIL_TAKEN',
  USERS_SELF_MUTATION_FORBIDDEN: 'USERS_SELF_MUTATION_FORBIDDEN',
  USERS_LAST_ADMIN: 'USERS_LAST_ADMIN',

  // Clients module
  CLIENTS_NOT_FOUND: 'CLIENTS_NOT_FOUND',
  CLIENTS_HAS_PROJECTS: 'CLIENTS_HAS_PROJECTS',
  CLIENTS_DELIVERY_ADDRESS_NOT_FOUND: 'CLIENTS_DELIVERY_ADDRESS_NOT_FOUND',

  // Projects module
  PROJECTS_NOT_FOUND: 'PROJECTS_NOT_FOUND',
  PROJECTS_INVALID_TRANSITION: 'PROJECTS_INVALID_TRANSITION',
  PROJECTS_REJECTION_DETAILS_REQUIRED: 'PROJECTS_REJECTION_DETAILS_REQUIRED',
  PROJECTS_BACKWARD_TRANSITION_FORBIDDEN: 'PROJECTS_BACKWARD_TRANSITION_FORBIDDEN',
  PROJECTS_WORKER_FORBIDDEN_FIELD: 'PROJECTS_WORKER_FORBIDDEN_FIELD',
  PROJECTS_CLIENT_NOT_FOUND: 'PROJECTS_CLIENT_NOT_FOUND',
  PROJECTS_DELIVERY_ADDRESS_INVALID: 'PROJECTS_DELIVERY_ADDRESS_INVALID',
  PROJECTS_ASSIGNMENT_NOT_FOUND: 'PROJECTS_ASSIGNMENT_NOT_FOUND',
  PROJECTS_ASSIGNEE_NOT_WORKER: 'PROJECTS_ASSIGNEE_NOT_WORKER',
  PROJECTS_ASSIGNEE_INACTIVE: 'PROJECTS_ASSIGNEE_INACTIVE',
  /** Every write on an archived project except the one that unarchives it. */
  PROJECTS_ARCHIVED_READ_ONLY: 'PROJECTS_ARCHIVED_READ_ONLY',
  /** Archiving something that is not under warranty, or unarchiving what is not archived. */
  PROJECTS_NOT_ARCHIVABLE: 'PROJECTS_NOT_ARCHIVABLE',
  /** Restoring or purging a project that is not in "Proiecte șterse". */
  PROJECTS_NOT_DELETED: 'PROJECTS_NOT_DELETED',
  /** A project that cannot be brought into a merge; the message says why. */
  PROJECTS_NOT_MERGEABLE: 'PROJECTS_NOT_MERGEABLE',

  // Catalog module
  CATALOG_NOT_FOUND: 'CATALOG_NOT_FOUND',
  CATALOG_SKU_TAKEN: 'CATALOG_SKU_TAKEN',
  CATALOG_NAME_TAKEN: 'CATALOG_NAME_TAKEN',
  CATALOG_INTERNAL_CODE_TAKEN: 'CATALOG_INTERNAL_CODE_TAKEN',
  CATALOG_TYPE_IMMUTABLE: 'CATALOG_TYPE_IMMUTABLE',
  CATALOG_IN_USE: 'CATALOG_IN_USE',
  CATALOG_ALREADY_PROMOTED: 'CATALOG_ALREADY_PROMOTED',

  // Project line-items
  PROJECT_ITEMS_NOT_FOUND: 'PROJECT_ITEMS_NOT_FOUND',
  PROJECT_ITEMS_PROJECT_TERMINAL: 'PROJECT_ITEMS_PROJECT_TERMINAL',
  PROJECT_ITEMS_CATALOG_INCOMPATIBLE: 'PROJECT_ITEMS_CATALOG_INCOMPATIBLE',
  PROJECT_ITEMS_DIMENSIONS_REQUIRED: 'PROJECT_ITEMS_DIMENSIONS_REQUIRED',
  PROJECT_ITEMS_CURRENCY_MISMATCH: 'PROJECT_ITEMS_CURRENCY_MISMATCH',

  // Operations scheduler
  SCHEDULER_WORKER_HAS_HISTORY: 'SCHEDULER_WORKER_HAS_HISTORY',
  /** The team is on a stage right now — as lead or as reinforcement. */
  SCHEDULER_TEAM_BUSY: 'SCHEDULER_TEAM_BUSY',
  /** The team still owns roster rows; `details.workers` counts them. */
  SCHEDULER_TEAM_HAS_WORKERS: 'SCHEDULER_TEAM_HAS_WORKERS',
  /** "Start now" found no free crew: every capable team is busy elsewhere. */
  SCHEDULER_NO_AVAILABLE_TEAM: 'SCHEDULER_NO_AVAILABLE_TEAM',
  /**
   * A crew picked off a preview is no longer in the situation the preview
   * showed (it went free, got busy elsewhere, or the next operation changed).
   */
  SCHEDULER_TEAM_CHOICE_CHANGED: 'SCHEDULER_TEAM_CHOICE_CHANGED',
  /**
   * A simulated project cannot be projected; `details.reason` says why
   * (schedulerSimulationInvalidReasons), `details.maxHours` for STAGE_TOO_LONG.
   */
  SCHEDULER_SIMULATION_INVALID: 'SCHEDULER_SIMULATION_INVALID',
  /** Reopen refused: not a finished production project; `details.reason` ∈ schedulerNotInWarrantyReasons. */
  SCHEDULER_NOT_IN_WARRANTY: 'SCHEDULER_NOT_IN_WARRANTY',
  /** Reopen refused: the 24-month warranty ended; `details.warrantyEndsAt`. */
  SCHEDULER_WARRANTY_EXPIRED: 'SCHEDULER_WARRANTY_EXPIRED',
  /** An intervention is already in progress; `details.interventionId`, `details.interventionNumber`. */
  SCHEDULER_INTERVENTION_OPEN: 'SCHEDULER_INTERVENTION_OPEN',
  /** A finished (COMPLETED/CANCELLED) scheduler row cannot be edited or cancelled. */
  SCHEDULER_PROJECT_FINISHED: 'SCHEDULER_PROJECT_FINISHED',
  /**
   * Not allowed on an intervention / a project under warranty; `details.reason`
   * ∈ schedulerInterventionLockedReasons (START_AFTER_WARRANTY also carries
   * `details.warrantyEndsAt`).
   */
  SCHEDULER_INTERVENTION_LOCKED: 'SCHEDULER_INTERVENTION_LOCKED',
  /**
   * A deadline recommendation's steps no longer hold against the board as it
   * is now (a crew moved, the project is on time, another project would get
   * later…); `details.problems` lists why (schedulerRescueProblemDto[]).
   */
  SCHEDULER_RESCUE_REJECTED: 'SCHEDULER_RESCUE_REJECTED',
  /** The crew is a collaborator reserved for another operation until that one is done. */
  SCHEDULER_TEAM_RESERVED: 'SCHEDULER_TEAM_RESERVED',
  /**
   * The leave overlaps another of the same worker's; `details.leaveId`,
   * `details.startDate` and `details.endDate` ('YYYY-MM-DD') are that one.
   */
  SCHEDULER_LEAVE_OVERLAP: 'SCHEDULER_LEAVE_OVERLAP',
  /** The leave ended before the scheduler's (virtual) today — only current or upcoming leave is recorded. */
  SCHEDULER_LEAVE_ENDED: 'SCHEDULER_LEAVE_ENDED',
  /**
   * Hours entered by hand on a project the worker's crew was not on in the
   * flow during that span, and is not on now. Nothing is saved; `details`
   * (workSessionMismatchDetailsDto) names the crew's projects and what the flow
   * would get, and re-sending with `confirmProjectMismatch` saves the entry.
   */
  SCHEDULER_SESSION_PROJECT_MISMATCH: 'SCHEDULER_SESSION_PROJECT_MISMATCH',

  // Rate limiting
  RATE_LIMITED: 'RATE_LIMITED',

  // Server
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const;
export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export interface ApiErrorBody {
  code: ErrorCode;
  message: string;
  details?: Record<string, unknown>;
  requestId?: string;
}
